import React, { useState, useEffect } from 'react';
import { Globe, Check } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Button } from './ui/button';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// Language configurations with proper Noto Sans font mappings - 16 languages
const LANGUAGE_CONFIG = {
  'en-IN': { display: 'English', font: 'Noto Sans', subset: '' },
  'hi-IN': { display: '\u0939\u093F\u0928\u094D\u0926\u0940', font: 'Noto Sans Devanagari', subset: 'devanagari' },
  'bn-IN': { display: '\u09AC\u09BE\u0982\u09B2\u09BE', font: 'Noto Sans Bengali', subset: 'bengali' },
  'ta-IN': { display: '\u0BA4\u0BAE\u0BBF\u0BB4\u0BCD', font: 'Noto Sans Tamil', subset: 'tamil' },
  'te-IN': { display: '\u0C24\u0C46\u0C32\u0C41\u0C17\u0C41', font: 'Noto Sans Telugu', subset: 'telugu' },
  'mr-IN': { display: '\u092E\u0930\u093E\u0920\u0940', font: 'Noto Sans Devanagari', subset: 'devanagari' },
  'gu-IN': { display: '\u0A97\u0AC1\u0A9C\u0AB0\u0ABE\u0AA4\u0AC0', font: 'Noto Sans Gujarati', subset: 'gujarati' },
  'kn-IN': { display: '\u0C95\u0CA8\u0CCD\u0CA8\u0CA1', font: 'Noto Sans Kannada', subset: 'kannada' },
  'ml-IN': { display: '\u0D2E\u0D32\u0D2F\u0D3E\u0D33\u0D02', font: 'Noto Sans Malayalam', subset: 'malayalam' },
  'or-IN': { display: '\u0B13\u0B21\u0B3C\u0B3F\u0B06', font: 'Noto Sans Oriya', subset: 'oriya' },
  'pa-IN': { display: '\u0A2A\u0A70\u0A1C\u0A3E\u0A2C\u0A40', font: 'Noto Sans Gurmukhi', subset: 'gurmukhi' },
  'as-IN': { display: '\u0985\u09B8\u09AE\u09C0\u09AF\u09BC\u09BE', font: 'Noto Sans Bengali', subset: 'bengali' },
  'ur-IN': { display: '\u0627\u0631\u062F\u0648', font: 'Noto Nastaliq Urdu', subset: '' },
  'sa-IN': { display: '\u0938\u0902\u0938\u094D\u0915\u0943\u0924\u092E\u094D', font: 'Noto Sans Devanagari', subset: 'devanagari' },
  'fr-FR': { display: 'Fran\u00E7ais', font: 'Noto Sans', subset: '' },
  'es-ES': { display: 'Espa\u00F1ol', font: 'Noto Sans', subset: '' }
};

const LanguageSelector = ({ onLanguageChange }) => {
  const { user } = useAuth();
  const [selectedLocale, setSelectedLocale] = useState('en-IN');
  const [supportedLanguages, setSupportedLanguages] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Fetch supported languages from backend
    const fetchLanguages = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/languages`);
        setSupportedLanguages(response.data.languages);
      } catch (error) {
        console.error('Failed to fetch languages:', error);
      }
    };

    fetchLanguages();

    // Load saved locale from localStorage
    const savedLocale = localStorage.getItem('preferredLocale');
    if (savedLocale && LANGUAGE_CONFIG[savedLocale]) {
      setSelectedLocale(savedLocale);
      applyFont(savedLocale);
      if (onLanguageChange) {
        onLanguageChange(savedLocale);
      }
    }
  }, [onLanguageChange]);

  const applyFont = (locale) => {
    const fontConfig = LANGUAGE_CONFIG[locale];
    if (fontConfig) {
      // Update CSS variable
      document.documentElement.style.setProperty('--app-font-family', fontConfig.font);
      
      // Load Google Fonts dynamically if needed
      if (!document.getElementById(`font-${locale}`)) {
        const link = document.createElement('link');
        link.id = `font-${locale}`;
        link.rel = 'stylesheet';
        link.href = `https://fonts.googleapis.com/css2?family=${fontConfig.font.replace(/ /g, '+')}:wght@400;500;600;700&display=swap`;
        document.head.appendChild(link);
      }
    }
  };

  const handleLanguageChange = async (locale) => {
    setLoading(true);
    
    try {
      // Update state FIRST for immediate UI update
      setSelectedLocale(locale);
      
      // Save to localStorage
      localStorage.setItem('preferredLocale', locale);
      
      // Apply font immediately
      applyFont(locale);
      
      // Notify parent component immediately
      if (onLanguageChange) {
        onLanguageChange(locale);
      }
      
      // Update user profile in database (async, non-blocking)
      if (user && user.userId) {
        axios.put(`${BACKEND_URL}/api/users/${user.userId}/locale`, 
          { locale },
          { params: { locale } }
        ).catch(err => console.error('Failed to update locale in DB:', err));
      }
    } catch (error) {
      console.error('Failed to update language:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="ghost" 
          size="sm" 
          className="h-9 px-3 gap-2"
          disabled={loading}
        >
          <Globe className="h-4 w-4" />
          <span className="hidden sm:inline">
            {LANGUAGE_CONFIG[selectedLocale]?.display || 'English'}
          </span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        <div className="px-2 py-1.5 text-sm font-semibold text-slate-900 dark:text-white">
          Select Language
        </div>
        <div className="h-px bg-slate-200 dark:bg-slate-700 my-1" />
        {Object.entries(LANGUAGE_CONFIG).map(([locale, config]) => (
          <DropdownMenuItem
            key={locale}
            onClick={() => handleLanguageChange(locale)}
            className="flex items-center justify-between cursor-pointer"
            style={{ fontFamily: config.font }}
          >
            <span>{config.display}</span>
            {selectedLocale === locale && (
              <Check className="h-4 w-4 text-blue-600" />
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default LanguageSelector;
